import React from "react";

import { useNavigate } from "react-router-dom";

import {
  Build as AutomationIcon,
  Security as SecurityIcon,
  GitHub as GitHubIcon,
  Visibility as ObservabilityIcon,
  Key as KeyIcon,
  KeyboardArrowRight as ArrowIcon,
  AccountTree as ArchitectureIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import InterestForm from '../components/interestForm';

export function Home() {
  const navigate = useNavigate();

  const features = [
    {
      title: "Control Your Rollouts",
      icon: <ArchitectureIcon fontSize="large" color="primary" />,
      description:
        "Manage deployment strategies and safely roll out changes with progressive delivery.",
    },
    {
      title: "Developer Platform",
      icon: <GitHubIcon fontSize="large" color="primary" />,
      description:
        "Backstage-powered portal with service catalog, templates, and documentation.",
    },
    {
      title: "Bring Your Own Support",
      icon: <KeyIcon fontSize="large" color="primary" />,
      description:
        "Scale your support levels based on your needs, from community to enterprise. 24/7 support available at additional cost.",
    },
    {
      title: "Robust Security",
      icon: <SecurityIcon fontSize="large" color="primary" />,
      description:
        "Enterprise-grade security with built-in compliance and best practices.",
    },
    {
      title: "Automated Workflows",
      icon: <AutomationIcon fontSize="large" color="primary" />,
      description:
        "Streamline CI/CD pipelines and focus on what matters most - your code.",
    },
    {
      title: "Full Observability",
      icon: <ObservabilityIcon fontSize="large" color="primary" />,
      description:
        "Monitor application health with integrated logging and metrics.",
    },
  ];

  const technologies = [
    "Kubernetes",
    "GitLab",
    "Vault",
    "cert-manager",
    "ArgoCD",
    "Backstage",
    "Matrix",
    "OpenTelemetry",
    "Prometheus",
    "Grafana",
    "Keycloak",
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          backgroundImage:
            "radial-gradient(circle at 25% 25%, rgba(67, 211, 255, 0.2), transparent 40%), radial-gradient(circle at 75% 75%, rgba(255, 150, 67, 0.2), transparent 40%)",
          py: { xs: 8, md: 12 },
          mb: 6,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    background: 'linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}
                >
                  Cloud-Native Infrastructure Made Simple
                </Typography>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  sx={{ fontWeight: 400, mb: 4, maxWidth: 600 }}
                >
                  Deploy and manage your applications with an all-in-one
                  platform built for developers, by developers.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowIcon />}
                    onClick={() => navigate("/signup")}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: '0 4px 14px 0 rgba(67, 211, 255, 0.3)',
                      background: 'linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #38b8df 0%, #3be07f 100%)',
                        boxShadow: '0 6px 20px 0 rgba(67, 211, 255, 0.4)',
                      }
                    }}
                  >
                    Get Started
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate("/pricing")}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderColor: "primary.main",
                      "&:hover": {
                        borderColor: "primary.light",
                        backgroundColor: "rgba(67, 211, 255, 0.1)",
                      },
                    }}
                  >
                    View Pricing
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/logo.svg`}
                alt="Microcumulus Logo"
                sx={{
                  width: "100%",
                  maxWidth: 400,
                  filter: "drop-shadow(0px 0px 30px rgba(67, 211, 255, 0.3))",
                  animation: "pulse 3s infinite ease-in-out",
                  "@keyframes pulse": {
                    "0%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.05)" },
                    "100%": { transform: "scale(1)" },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Grid container spacing={4} justifyContent="center">
          {[
            { value: "100%", label: "Control" },
            { value: "20+", label: "Dev Tools" },
            { value: "2x", label: "Faster Deployments" },
            { value: "BYO", label: "Support Options" },
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  textAlign: "center",
                  height: "100%",
                  p: 2,
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3
                }}
              >
                <CardContent>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700, 
                      background: 'linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Why Choose Microcumulus?
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto" }}
          >
            Our platform offers everything you need to build, deploy, and scale
            your applications
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Technologies Section */}
      <Box
        sx={{
          backgroundColor: "background.paper",
          py: 10,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 10,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{ textAlign: "center", mb: 6, fontWeight: 600 }}
          >
            Built on Industry-Leading Technologies
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            {technologies.map((tech, index) => (
              <Grid item key={index}>
                <Chip label={tech} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Interest Form Section */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                height: "100%",
                borderRadius: 4,
                textAlign: "center",
                background:
                  "linear-gradient(135deg, rgba(26, 30, 35, 0.95) 0%, rgba(43, 49, 55, 0.95) 100%)",
                border: "1px solid",
                borderColor: "divider",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.1,
                  background:
                    "radial-gradient(circle at 70% 20%, rgba(67, 211, 255, 0.8), transparent 60%)",
                }}
              />

              <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                Ready to Transform Your Cloud Infrastructure?
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 700, mx: "auto" }}
              >
                Join developers who are using Microcumulus to streamline
                deployments and accelerate development. Contact us at
                andrew@microcumul.us for a free trial or demo.
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/signup")}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  boxShadow: "0 4px 14px 0 rgba(67, 211, 255, 0.3)",
                  alignSelf: "center",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                    boxShadow: "0 6px 20px 0 rgba(67, 211, 255, 0.4)",
                  },
                }}
              >
                Get Started
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <InterestForm />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// Custom chip component for technologies
function Chip({ label }: { label: string }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 3,
        py: 1.5,
        borderRadius: 4,
        backgroundColor: "rgba(67, 211, 255, 0.1)",
        border: "1px solid",
        borderColor: "rgba(67, 211, 255, 0.3)",
        m: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: "rgba(67, 211, 255, 0.2)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
}
